// @flow
import * as React from 'react';
import { MasterVehicleModel } from 'common/models/internal/MasterVehicleModel';
import type { ThemeType } from 'react-css-themr';
import { Button, Columns, Text } from '@locus-taxy/neo-ui';
import Section from './Section';
import { VehicleCompartment } from 'common/models/internal/VehicleCompartment';
import VehicleCompartmentInput from './VehicleCompartmentInput';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';

type Props = {
    entity: MasterVehicleModel,
    disabled: boolean,
    field: $FixMe,
    onBlur: ?() => void,
    onChange: (value: $FixMe, fieldIndex?: number) => void,
    testId?: string,
    theme: ThemeType,
    // extraProps
    unitConverter: UnitConversionHelper,
    t: TFunction
};

export const VehicleCompartmentsInput = (props: Props): React.Node => {
    const { theme, entity, onChange, unitConverter } = props;
    const { t } = useTranslation('entityManagement');
    const [compartmentVisibility, setCompartmentVisibility] = React.useState(
        {}
    );

    const onCompartmentCollapse = React.useCallback(
        (sectionId: string, collapsed: boolean) => {
            setCompartmentVisibility(compartmentVisibility => ({
                ...compartmentVisibility,
                [sectionId]: collapsed
            }));
        },
        []
    );

    const getSectionName = React.useCallback(
        index => {
            return `${t(
                'masterVehicleModelProperties.compartments.compartment'
            )} ${index + 1}`;
        },
        [t]
    );

    const addNewCompartment = React.useCallback(() => {
        const newCompartment = VehicleCompartment.defaultInit();
        const volume = newCompartment.volume.setUnit(entity.volume.unit);
        const updateCompartment = newCompartment.setVolume(volume);
        const updateEntity = entity.setCompartments(
            entity.compartments.push(updateCompartment)
        );
        onChange(updateEntity);
    }, [entity, onChange]);

    const deleteCompartment = React.useCallback(
        compartmentName => {
            const updateEntity = entity.setCompartments(
                entity.compartments.filter(
                    (compartment, index) =>
                        compartmentName !== getSectionName(index)
                )
            );
            onChange(updateEntity);
        },
        [entity, getSectionName, onChange]
    );

    const duplicateCompartment = React.useCallback(
        compartmentName => {
            const compartmentToDuplicate = entity.compartments.find(
                (compartment, index) =>
                    compartmentName === getSectionName(index)
            );
            if (compartmentToDuplicate) {
                const updateEntity = entity.setCompartments(
                    entity.compartments.push(compartmentToDuplicate)
                );
                onChange(updateEntity);
            }
        },
        [entity, getSectionName, onChange]
    );

    const onCompartmentChange = React.useCallback(
        (updatedCompartment: VehicleCompartment, index: number) => {
            onChange(
                entity.setCompartments(
                    entity.compartments.set(index, updatedCompartment)
                )
            );
        },
        [entity, onChange]
    );

    return (
        <Section id="vehicleCompartments">
            {entity.compartments.map((compartment, index) => {
                const compartmentFormTitle = getSectionName(index);
                return (
                    <VehicleCompartmentInput
                        theme={theme}
                        key={index}
                        compartmentIndex={index}
                        compartment={compartment}
                        onDelete={deleteCompartment}
                        onDuplicate={duplicateCompartment}
                        onCollapse={onCompartmentCollapse}
                        collapsed={compartmentVisibility[compartmentFormTitle]}
                        onChange={onCompartmentChange}
                        unitConverter={unitConverter}
                        t={t}
                    />
                );
            })}
            {entity.compartments.isEmpty() ? (
                <Section id="no-compartements">
                    <Columns alignY="center" align="center">
                        <Text>{t('vehicleModelForm.noCompartment')}</Text>
                    </Columns>
                </Section>
            ) : null}
            <Columns>
                <Button
                    icon="add"
                    appearance="ghost"
                    size="small"
                    palette="primary"
                    onClick={addNewCompartment}
                    sx={{
                        border: 'none',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            border: 'none'
                        }
                    }}
                >
                    {t('vehicleModelForm.addCompartment')}
                </Button>
            </Columns>
        </Section>
    );
};

export default VehicleCompartmentsInput;
